import { useFormikContext } from "formik";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Code, Flex, Text } from "@radix-ui/themes";
import { object, string, boolean, InferType } from "yup";
import InputField from "@/components/form/InputField";
import SelectField from "@/components/form/SelectField";
import SearchableSelectField from "@/components/form/SearchableSelectField";
import CheckboxField from "@/components/form/CheckboxField";
import { useEntities } from "@/hooks/useCommonQueries";
import { fieldService } from "@/services/breeze/FieldService";

export const fileHandlerValidationSchema = object({
  parameters: object().shape({
    operation: string()
      .required("Operation is required")
      .oneOf(["fetch_and_link", "link_existing", "detach"], "Invalid operation"),
    entity_name: string().required("Entity is required"),
    record_id: string().required("Record id is required"),
    field_name: string()
      .required("Field name is required")
      .matches(/^[a-z_][a-z0-9_]*$/, "Field name must be snake_case"),
    source_url: string().when("operation", {
      is: "fetch_and_link",
      then: (s) => s.required("Source URL is required for fetch_and_link"),
      otherwise: (s) => s.notRequired(),
    }),
    existing_file_id: string().when("operation", {
      is: "link_existing",
      then: (s) => s.required("Existing file id is required for link_existing"),
      otherwise: (s) => s.notRequired(),
    }),
    file_name: string(),
    folder_id: string(),
    is_public: boolean(),
    link_mode: string().oneOf(["set", "add"]),
    overwrite_if_present: boolean(),
  }),
});

export type FileHandlerFormValues = InferType<typeof fileHandlerValidationSchema>;

export default function FileHandlerFields() {
  const { values } = useFormikContext<FileHandlerFormValues>();
  const operation = values.parameters?.operation;
  const entityName = values.parameters?.entity_name;

  const { data: entities } = useEntities();
  const entityOptions = useMemo(
    () => entities?.map((e) => ({ value: e.name, label: e.name })) || [],
    [entities],
  );

  const { data: fields = [] } = useQuery({
    queryKey: ["entity-fields", entityName],
    queryFn: () => (entityName ? fieldService.getAllByEntityName(entityName) : Promise.resolve([])),
    enabled: !!entityName,
  });

  const fileFieldOptions = useMemo(
    () =>
      fields
        .filter((f) => f.database_type === "file")
        .map((f) => ({ value: f.name, label: f.name })),
    [fields],
  );

  return (
    <Flex direction="column" gap="3">
      <SelectField name="parameters.operation" label="Operation" required type="string">
        <SelectField.Item value="fetch_and_link">Fetch and link</SelectField.Item>
        <SelectField.Item value="link_existing">Link existing file</SelectField.Item>
        <SelectField.Item value="detach">Detach</SelectField.Item>
      </SelectField>

      <SearchableSelectField
        name="parameters.entity_name"
        label="Entity"
        required
        options={entityOptions}
        searchPlaceholder="Search entities..."
        placeholder="Select entity..."
      />

      <InputField
        name="parameters.record_id"
        label="Record ID (templatable, typically {{ $anythink.trigger.id }})"
        required
      />

      <SearchableSelectField
        name="parameters.field_name"
        label="File field"
        required
        options={fileFieldOptions}
        searchPlaceholder="Search file fields..."
        placeholder={entityName ? "Select file field..." : "Pick an entity first"}
      />

      {operation === "fetch_and_link" && (
        <>
          <InputField name="parameters.source_url" label="Source URL (templatable)" required />
          <InputField name="parameters.file_name" label="File name (optional, templatable)" />
          <InputField name="parameters.folder_id" label="Folder ID (optional, templatable)" />
          <CheckboxField name="parameters.is_public" label="Public" />
          <CheckboxField
            name="parameters.overwrite_if_present"
            label="Overwrite if field already has a file"
          />
        </>
      )}

      {operation === "link_existing" && (
        <>
          <InputField
            name="parameters.existing_file_id"
            label="Existing file ID (templatable)"
            required
          />
          <SelectField name="parameters.link_mode" label="Link mode" type="string">
            <SelectField.Item value="set">Set (replace existing)</SelectField.Item>
            <SelectField.Item value="add">Add (append, dedupe)</SelectField.Item>
          </SelectField>
        </>
      )}

      <Box>
        <Text size="2" color="gray">
          All string fields support <Code size="1">{`{{ … }}`}</Code> templating
          (e.g. <Code size="1">{`{{ $anythink.trigger.id }}`}</Code>).
        </Text>
      </Box>
    </Flex>
  );
}
